import React, { useCallback, useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  fastPlaylistColumns,
  getFastPlaylistHref,
} from "./tableParams/fastPlaylistColumns";
import FastAPI from "../../../api/FastAPI";
import useFASTLayout from "../../../hooks/useFASTLayout";
import CreateButton from "../../common/CreateButton";
import DataTable from "../../dataTable/DataTable";
import { fastPlaylistEditPath, fastPlaylistNewPath } from "../../routes/routes";

import type { JSX } from "react";

import type { Playlist } from "../../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";

export default function FASTPlaylists(): JSX.Element {
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { t } = useTranslation("editor-fast-channel");

  useFASTLayout({
    title: t("fast-playlists"),
  });

  const getPlaylistListQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylistList,
    params: [channelId],
  });

  const { data, isLoading, isError, refetch } = useQuery(getPlaylistListQuery);

  const { mutate: deletePlaylistMutate } = useMutation({
    mutationFn: async (playlistId: string) =>
      FastAPI.deletePlaylist({ channelId, playlistId }),
    onSuccess: () => {
      toast.success(t("playlistDeleteSuccess"));
      queryClient.invalidateQueries(getPlaylistListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("playlistDeleteError"));
      console.error(e);
    },
  });

  const { mutate: duplicatePlaylistMutate } = useMutation({
    mutationFn: async (playlistId: string) =>
      FastAPI.duplicatePlaylist(channelId, playlistId),
    onSuccess: () => {
      toast.success(t("playlistDuplicateSuccess"));
      queryClient.invalidateQueries(getPlaylistListQuery.queryKey);
    },
    onError: (e) => {
      toast.error(t("playlistDuplicateError"));
      console.error(e);
    },
  });

  const getRowHref = useCallback(
    (playlist: Playlist): string => getFastPlaylistHref(channelId, playlist),
    [channelId],
  );

  const columnList = useMemo(
    () =>
      fastPlaylistColumns({
        t,
        channelId,
        onDuplicate: (playlist) => duplicatePlaylistMutate(playlist.uuid),
        onEdit: (playlist) =>
          history.push(fastPlaylistEditPath(channelId, playlist.uuid)),
        onDelete: (playlist) => deletePlaylistMutate(playlist.uuid),
      }),
    [t, channelId, history, duplicatePlaylistMutate, deletePlaylistMutate],
  );

  const createPlaylistButton = (
    <CreateButton
      href={fastPlaylistNewPath(channelId)}
      label={t("createFASTPlaylist")}
    />
  );

  return (
    <div className="w-full min-w-0">
      <DataTable
        columns={columnList}
        data={data?.data ?? []}
        defaultSorting={{ id: "updated_at", desc: true }}
        getRowHref={getRowHref}
        headerActions={createPlaylistButton}
        isError={isError}
        isLoading={isLoading}
        onRetry={refetch}
        title={t("fastPlaylistListTitle")}
      />
    </div>
  );
}
